import React, { useMemo, useState } from "react";
import { SERVICES, SERVICE_FLOWS, getServiceLabel } from "./config";

import Stepper from "./ui/Stepper";
import SelectCard from "./ui/SelectCard";
import SummaryBar from "./ui/SummaryBar";
import API from "../../utils/api";
import OptionsStep from "./steps/OptionsStep";
import AppointmentStep from "./steps/AppointmentStep";
import ContactStep from "./steps/ContactStep";
import MultiOptionsStep from "./steps/MultiOptionsStep";
import { LeftIcon, ArrowSmallIcon } from "./icons";

const QuoteWizard = () => {
  // Screen 0 = service picker (your center 5 cards screen)
  // After selecting service → stepIndex = 0 (first step in that service flow)
  const [activeService, setActiveService] = useState("");
  const [stepIndex, setStepIndex] = useState(0);

  // generic answers store (works for any service/any steps)
  const [answers, setAnswers] = useState({
    // contact fields can live here too
    company: "",
    contact: "",
    email: "",
    phone: "+49 ",
    accept: false,
  });

  const steps = useMemo(() => {
    if (!activeService) return [];
    return SERVICE_FLOWS[activeService]?.steps || [];
  }, [activeService]);

  const showStepper = !!activeService; // show only after service selected

  const currentStep = steps[stepIndex];

  function goPrev() {
    if (!activeService) return;
    if (stepIndex === 0) {
      // go back to service selection screen
      setActiveService("");
      setStepIndex(0);
      return;
    }
    setStepIndex((i) => Math.max(0, i - 1));
  }

  function goNext() {
    if (!activeService) return;
    setStepIndex((i) => Math.min(steps.length - 1, i + 1));
  }

  function resetWizard() {
    setActiveService("");
    setStepIndex(0);
    setAnswers({
      company: "",
      contact: "",
      email: "",
      phone: "+49",
      accept: false,
    });
  }

  function setField(field, value) {
    setAnswers((prev) => ({ ...prev, [field]: value }));
  }
  function buildPayloadForService(serviceKey, answers) {
    const flow = SERVICE_FLOWS[serviceKey];
    if (!flow) return { service: serviceKey, answers: {} };

    const picked = {};
    const keep = new Set(["company", "contact", "email", "phone", "accept"]);

    flow.steps.forEach((st) => {
      if (st.type === "options" || st.type === "multiOptions") {
        if (st.field) keep.add(st.field);
      }
      if (st.type === "appointment" && st.fields) {
        keep.add(st.fields.date);
        keep.add(st.fields.time);
        keep.add(st.fields.duration);
      }
    });

    Object.keys(answers).forEach((k) => {
      if (keep.has(k)) picked[k] = answers[k];
    });

    return { service: serviceKey, answers: picked };
  }

 async function handleFinalSubmit() {
  if (!answers.company || !answers.contact || !answers.email || !answers.accept) {
    alert("Bitte füllen Sie alle Pflichtfelder aus und akzeptieren Sie den Datenschutz.");
    return;
  }

  const payload = buildPayloadForService(activeService, answers);

  try {
    const res = await API.post("/contact3", payload); // ✅ send payload directly

    // axios returns response data here
    // console.log(res.data);

    alert("Danke! Wir senden Ihnen das Angebot in Kürze per Mail.");
    resetWizard();
  } catch (e) {
    const msg =
      e?.response?.data?.message || e?.message || "Etwas ist schief gelaufen.";
    alert(msg);
  }
}



  // Build summary automatically (optional)
  const summaryItems = useMemo(() => {
    if (!activeService) return [];

    const flow = SERVICE_FLOWS[activeService];
    if (!flow) return [];

    const items = [{ label: "Leistung", value: getServiceLabel(activeService) || "—" }];

    flow.steps.forEach((step) => {
      // single select
      if (step.type === "options" && step.field && answers[step.field]) {
        const opt = step.options?.find((o) => o.key === answers[step.field]);
        items.push({
          label: step.title,
          value: opt?.label || String(answers[step.field]),
        });
      }

      // multi select
      if (step.type === "multiOptions" && step.field && Array.isArray(answers[step.field])) {
        if (answers[step.field].length) {
          const labels = step.options
            ?.filter((o) => answers[step.field].includes(o.key))
            .map((o) => o.label)
            .join(", ");

          items.push({
            label: step.title,
            value: labels,
          });
        }
      }

      // appointment
      if (step.type === "appointment" && step.fields) {
        const { date, time, duration } = step.fields;

        if (answers[date]) items.push({ label: "Datum", value: answers[date] });
        if (answers[time]) items.push({ label: "Uhrzeit", value: answers[time] });
        if (answers[duration]) items.push({ label: "Dauer", value: `${answers[duration]} Min` });
      }
    });

    return items;
  }, [activeService, answers]);



  function renderStep() {
    if (!activeService) return null;
    if (!currentStep) return null;

    if (currentStep.type === "options") {
      return (
        <OptionsStep
          step={currentStep}
          value={answers[currentStep.field] || ""}
          onSelect={(key) => {
            setField(currentStep.field, key);
            // auto-next like your current behavior
            if (stepIndex < steps.length - 1) goNext();
          }}
        />
      );
    }
    if (currentStep.type === "multiOptions") {
      const arr = Array.isArray(answers[currentStep.field]) ? answers[currentStep.field] : [];

      return (
        <MultiOptionsStep
          step={currentStep}
          values={arr}
          onToggle={(key) => {
            const exists = arr.includes(key);
            const next = exists ? arr.filter((x) => x !== key) : [...arr, key];
            setField(currentStep.field, next);
          }}
          onContinue={() => {
            // optional validation if you want minimum selected
            const min = currentStep.minSelected ?? 0;
            if (arr.length < min) {
              alert(`Bitte wählen Sie mindestens ${min} Option(en).`);
              return;
            }
            goNext();
          }}
        />
      );
    }


    if (currentStep.type === "appointment") {
      return (
        <AppointmentStep
          step={currentStep}
          answers={answers}
          setField={setField}
          onContinue={() => {
            // require all 3 fields before continue
            const dateKey = currentStep.fields.date;
            const timeKey = currentStep.fields.time;
            const durKey = currentStep.fields.duration;

            if (!answers[dateKey] || !answers[timeKey] || !answers[durKey]) {
              alert("Bitte wählen Sie Datum, Uhrzeit und Dauer.");
              return;
            }
            goNext();
          }}
        />
      );
    }

    if (currentStep.type === "contact") {
      return (
        <ContactStep
          summaryItems={summaryItems}
          answers={answers}
          setField={setField}
          onBack={goPrev}
          onSubmit={handleFinalSubmit}
          submitLabel="Anfrage senden"
          submitIcon={<ArrowSmallIcon />}
        />
      );
    }

    return null;
  }

  return (
    <section className="w-full bg-white mt-12">
      <div className="mx-auto w-[90%] 2xl:w-[70%] py-10 md:py-14">
        <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-[0_24px_70px_-30px_rgba(0,0,0,0.18)]">
          {/* Header */}
          <div className="relative border-b border-gray-200 bg-[#0b2b40] px-6 py-7 md:px-10">
            <div className="absolute inset-0 opacity-30 [background-image:radial-gradient(circle_at_20%_10%,rgba(16,185,129,0.35),transparent_45%),radial-gradient(circle_at_90%_40%,rgba(132,204,22,0.25),transparent_35%)]" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white/90 ring-1 ring-white/15">
                In nur einer Minute
                <span className="h-1 w-1 rounded-full bg-emerald-300" />
                Unverbindlich
              </div>
              <h2 className="mt-3 h2-mb h2-md h2-lg h2-2xl !text-white ">
                Für welche Leistung wünschen Sie ein Angebot?
              </h2>
              <p className="mt-2 max-w-3xl text-sm text-white/75">
                Wählen Sie Schritt für Schritt aus, was Sie benötigen – danach erhalten Sie Ihr unverbindliches Angebot in Kürze per Mail.
              </p>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 py-7 md:px-10 md:py-10">
            {/* Stepper (dynamic) */}
            {showStepper && (
              <div className="mb-8">
                <Stepper stepIndex={stepIndex} steps={steps} />
              </div>
            )}

            {/* Back + counter */}
            {activeService && (
              <div className="mb-6 flex items-center justify-between">
                <button
                  type="button"
                  onClick={goPrev}
                  className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  <LeftIcon />
                  Zurück
                </button>

                <div className="text-xs text-slate-500">
                  Schritt {stepIndex + 1} von {steps.length}
                </div>
              </div>
            )}

            {/* Screen 0: Service picker (ALL 5 services like your image) */}
            {!activeService && (
              <div>
                <div className="mb-5">
                  <h3 className="text-lg font-semibold text-slate-900">Start</h3>
                  <p className="mt-1 text-sm text-slate-600">
                    Wählen Sie eine Leistung aus, um zu beginnen.
                  </p>
                </div>

                {/* Keep your “center” vibe. This layout does 4 on top, 1 below on large screens */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
                  {SERVICES.slice(0, 5).map((s) => {
                    const Icon = s.icon;
                    return (
                      <SelectCard
                        key={s.key}
                        title={s.label}
                        subtitle="Auswählen"
                        icon={<Icon />}
                        selected={false}
                        onClick={() => {
                          setActiveService(s.key);
                          setStepIndex(0);
                        }}
                      />
                    );
                  })}
                </div>


                {/* Optional: small preview summary area */}

              </div>
            )}

            {/* Service steps */}
            {activeService && (
              <div>
                {/* Optional summary above each step */}


                {renderStep()}
              </div>
            )}
          </div>

          {/* Footer mini */}
          <div className="border-t border-gray-200 bg-white px-6 py-4 md:px-10">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-xs text-slate-500"></div>

              <button
                type="button"
                onClick={resetWizard}
                className="inline-flex items-center justify-center rounded-full border border-gray-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                Zurücksetzen
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuoteWizard;
